import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import { ASSISTANT_COPY } from '../../constants/assistantCopy';
import composioService from '../../services/composioService';
import ComposioConnectionDialog from '../ToolsConfigure/ComposioConnectionDialog';
import AssistantPage, { AssistantResult } from './AssistantPage';

const SEARCH_PAGE_SIZE = 50;

/**
 * `ASSISTANT_TOOLKIT_CONNECT_PAGE_URL` → /assistant/connect/{toolkit}.
 *
 * For apps that need an API key rather than a sign-in, so the key is never
 * typed into a chat. The Integrations screen's connect dialog is reused as it
 * is: it loads the app's auth fields and connects through
 * POST /composio/connect/{app_id}. It calls `onRefresh` only after a
 * successful connect (and `onConnected` after a one-click sign-in), while a
 * plain `onClose` means the user closed it — that is how the two are told apart.
 */
const AssistantToolkitConnect = () => {
  const { toolkit } = useParams();
  const { has, ready } = usePermissions();
  // Fails open while permissions load; the API answers 403 regardless.
  const permitted = !ready || has(PERMISSIONS.COMPOSIO);

  const [app, setApp] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | notFound | failed | forbidden
  const [dialogOpen, setDialogOpen] = useState(false);
  const [outcome, setOutcome] = useState(null); // null | connected | cancelled

  const loadApp = useCallback(async () => {
    setState('loading');
    try {
      const data = await composioService.getAvailableApps({
        search: toolkit,
        page_size: SEARCH_PAGE_SIZE,
      });
      const wanted = String(toolkit || '').toLowerCase();
      // Search is fuzzy — only the app whose key is exactly the slug will do.
      const match = (data?.apps || []).find((a) => String(a?.key || '').toLowerCase() === wanted);
      if (!match) {
        setState('notFound');
        return;
      }
      setApp(match);
      setState('ready');
      setDialogOpen(true);
    } catch (err) {
      setState(err?.status === 403 ? 'forbidden' : 'failed');
    }
  }, [toolkit]);

  useEffect(() => {
    if (permitted) loadApp();
  }, [permitted, loadApp]);

  const markConnected = useCallback(() => setOutcome('connected'), []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    // After a successful connect the dialog closes itself and then calls
    // onRefresh, which overrides this.
    setOutcome((o) => (o === 'connected' ? o : 'cancelled'));
  }, []);

  let content;
  if (!permitted || state === 'forbidden') {
    content = (
      <AssistantResult
        tone="error"
        title={ASSISTANT_COPY.noIntegrations}
        hint={ASSISTANT_COPY.noIntegrationsHint}
      />
    );
  } else if (outcome === 'connected') {
    content = (
      <AssistantResult
        tone="success"
        title={ASSISTANT_COPY.connected(app?.name)}
        hint={ASSISTANT_COPY.connectedHint}
      />
    );
  } else if (state === 'notFound') {
    content = (
      <AssistantResult
        tone="error"
        title={ASSISTANT_COPY.appNotFound(toolkit)}
        hint={ASSISTANT_COPY.appNotFoundHint}
      />
    );
  } else if (state === 'failed') {
    content = (
      <AssistantResult
        tone="error"
        title={ASSISTANT_COPY.appsLoadFailed}
        actions={
          <Button variant="contained" onClick={loadApp}>
            Try again
          </Button>
        }
      />
    );
  } else if (outcome === 'cancelled') {
    content = (
      <AssistantResult
        tone="info"
        title={ASSISTANT_COPY.connectCancelled}
        hint={ASSISTANT_COPY.connectCancelledHint}
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setOutcome(null);
              setDialogOpen(true);
            }}
          >
            Enter the key
          </Button>
        }
      />
    );
  } else {
    content = (
      <AssistantResult
        tone="loading"
        title={app ? `Connect ${app.name || toolkit}` : 'Loading the app…'}
      />
    );
  }

  return (
    <AssistantPage>
      {content}
      {app && (
        <ComposioConnectionDialog
          open={dialogOpen && outcome !== 'connected'}
          onClose={handleClose}
          app={app}
          onConnect={(appId, connectData) => composioService.connectApp(appId, connectData)}
          isConnected={false}
          onRefresh={markConnected}
          onConnected={markConnected}
        />
      )}
    </AssistantPage>
  );
};

export default AssistantToolkitConnect;
